/**
 * Flow configurations for MultiStepFormModal.
 * Three intakes:
 *   - culture-files
 *   - events
 *   - reenactment
 *
 * Each config exports: { kind, intro, steps, buildPayload, success }
 */

const CULTURE_TAGS = [
  "motorcycle",
  "car",
  "trucking",
  "boating",
  "club",
  "crime",
  "law_enforcement",
  "betrayal",
  "loss",
  "comeback",
  "emotional",
  "viral",
  "build",
  "garage",
  "promoter",
];

const TALENT_TAGS = [
  "biker_look",
  "luxury_look",
  "rider",
  "driver",
  "mechanic",
  "fabricator",
  "promoter",
  "club_member",
  "speaks_spanish",
  "stunt",
  "on_camera",
];

// ============================================================
// Culture Files
// ============================================================
export const CULTURE_FILES_CONFIG = {
  kind: "culture-files",
  intro: {
    eyebrow: "CULTURE FILES",
    headline: "Culture Files: Tell the Story Only You Can Tell",
    blurb:
      "Real people, real moments. Interviews, documentary storytelling, and cinematic reenactments — shot at events, in garages, and live environments.",
    cta: "Start My Story Submission",
  },
  submitLabel: "Submit My Story",
  steps: [
    {
      key: "about",
      title: "About You",
      subtitle: "How we reach out when a producer wants to know more.",
      fields: [
        { key: "full_name", label: "Full Name", type: "text", required: true, maxLength: 120 },
        { key: "preferred_name", label: "Preferred Name", type: "text", maxLength: 80 },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", type: "tel", required: true, maxLength: 40 },
        { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
        { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
        { key: "age", label: "Age", type: "number", required: true, min: 18, max: 99 },
        { key: "social_handles", label: "Social Handles", type: "text", placeholder: "@you · ig / tt / yt", maxLength: 200 },
        {
          key: "contact_permission",
          label: "Can a producer reach out at the email/phone above?",
          type: "radio",
          required: true,
          options: ["Yes", "No"],
        },
      ],
    },
    {
      key: "culture",
      title: "Your Connection",
      fields: [
        {
          key: "culture_connection",
          label: "How are you connected to the culture? (pick all that apply)",
          type: "checkboxes",
          required: true,
          options: ["motorcycle", "car", "trucking", "boating", "racing", "builds", "garage", "street", "promoter", "other"],
        },
        { key: "club_affiliation", label: "Club Affiliation", type: "text", placeholder: "e.g., Ruff Ryders MC, Stance Nation", maxLength: 160 },
        { key: "club_details", label: "Club Details (optional)", type: "textarea", maxLength: 600, rows: 3 },
        { key: "years_in_culture", label: "Years in the Culture", type: "number", required: true, min: 0, max: 80 },
      ],
    },
    {
      key: "story",
      title: "Your Story",
      subtitle: "Be specific. Names can stay private. Don't write a synopsis — talk to us.",
      fields: [
        { key: "story_summary", label: "Tell your story", type: "textarea", required: true, maxLength: 3000, rows: 6 },
        { key: "story_power", label: "What makes it powerful?", type: "textarea", maxLength: 1000, rows: 3 },
        { key: "story_moment", label: "The moment that changed everything", type: "textarea", maxLength: 1000, rows: 3 },
        { key: "story_people", label: "Who else was involved?", type: "textarea", maxLength: 800, rows: 3 },
        {
          key: "story_categories",
          label: "Story categories (pick all that apply)",
          type: "checkboxes",
          options: ["betrayal", "law_enforcement", "loss", "comeback", "wins", "club_history", "near_death", "viral", "love_story", "other"],
        },
        {
          key: "previously_told",
          label: "Has this story been told publicly before?",
          type: "radio",
          required: true,
          options: ["No, first time", "Some on social", "Press coverage", "Yes, fully"],
        },
        {
          key: "on_camera_comfort",
          label: "Comfort on camera",
          type: "radio",
          required: true,
          options: ["Very comfortable", "OK", "Audio only", "Reenact only"],
        },
        {
          key: "tags",
          label: "Tags (help our editors find this story later)",
          type: "tags",
          tagPool: CULTURE_TAGS,
        },
      ],
    },
    {
      key: "legal",
      title: "Legal & Safety",
      subtitle:
        "Culture Files often includes cinematic reenactments at events, in garages, and on real locations. Read carefully.",
      fields: [
        {
          key: "legal_safe_to_share",
          label: "Is it currently safe and legal for you to share this story?",
          type: "radio",
          required: true,
          options: ["Yes", "No", "I want to discuss with a producer first"],
        },
        {
          key: "legal_active_case",
          label: "Are there any active legal cases tied to this story?",
          type: "radio",
          required: true,
          options: ["No", "Yes, closed", "Yes, ongoing"],
        },
        {
          key: "reenactment_aware",
          label:
            "I understand parts of my story may be filmed as a reenactment using actors, locations, and event footage. (acknowledgement)",
          type: "radio",
          required: true,
          options: ["I understand", "I want details first"],
        },
        {
          key: "anonymity",
          label: "Anonymity preference",
          type: "radio",
          required: true,
          options: ["Use my real name", "Pseudonym", "Voice modulated", "Discuss"],
        },
      ],
    },
    {
      key: "uploads",
      title: "Proof & Footage",
      subtitle:
        "Photos and short video help producers move faster. No filters — we want the real you.",
      fields: [
        {
          key: "photos",
          label: "Photos (no filters)",
          type: "photos",
          minPhotos: 0,
          maxPhotos: 8,
          note: "Mix of portrait and lifestyle. Phone photos are fine.",
        },
        {
          key: "video",
          label: "1–2 min intro video (or paste a link)",
          type: "video",
          required: true,
          note: "Tell us who you are and the headline of your story. Phone camera is fine.",
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: f.tags || [],
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      preferred_name: f.preferred_name || "",
      age: Number(f.age) || null,
      social_handles: f.social_handles || "",
      contact_permission: f.contact_permission || "",
      culture_connection: f.culture_connection || [],
      club_affiliation: f.club_affiliation || "",
      club_details: f.club_details || "",
      years_in_culture: Number(f.years_in_culture) || 0,
      story_summary: f.story_summary || "",
      story_power: f.story_power || "",
      story_moment: f.story_moment || "",
      story_people: f.story_people || "",
      story_categories: f.story_categories || [],
      previously_told: f.previously_told || "",
      on_camera_comfort: f.on_camera_comfort || "",
      legal_safe_to_share: f.legal_safe_to_share || "",
      legal_active_case: f.legal_active_case || "",
      reenactment_aware: f.reenactment_aware || "",
      anonymity: f.anonymity || "",
    },
  }),
  success: {
    title: "Story received.",
    message:
      "A producer will read it. If your story sparks something, we'll reach out within 14 days.",
  },
};

// ============================================================
// Events: We Outside
// ============================================================
export const EVENTS_CONFIG = {
  kind: "events",
  intro: {
    eyebrow: "WE OUTSIDE",
    headline: "If it's lit… we pull up.",
    blurb:
      "Submit your event to We Outside. Standout events become filming locations for Culture Files, reenactment scenes, casting, and sponsor activations.",
    cta: "Submit Your Event to We Outside",
  },
  submitLabel: "Submit Event",
  steps: [
    {
      key: "about",
      title: "Event Basics",
      fields: [
        { key: "event_name", label: "Event Name", type: "text", required: true, maxLength: 120 },
        { key: "full_name", label: "Organizer Name", type: "text", required: true, maxLength: 120 },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", type: "tel", required: true, maxLength: 40 },
        { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
        { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
      ],
    },
    {
      key: "details",
      title: "Event Details",
      fields: [
        {
          key: "event_type",
          label: "Event Type",
          type: "checkboxes",
          required: true,
          options: ["mc_annual", "car_meet", "truck_show", "boat_party", "ride_out", "day_party", "night_event", "party_in_the_pit", "other"],
        },
        {
          key: "frequency",
          label: "Frequency",
          type: "radio",
          required: true,
          options: ["one-off", "monthly", "quarterly", "annual", "multiple per year"],
        },
        { key: "attendance", label: "Typical attendance (people)", type: "number", required: true, min: 1, max: 100000 },
        { key: "uniqueness", label: "What makes your event unique?", type: "textarea", required: true, maxLength: 1500, rows: 4 },
        { key: "vibe", label: "Describe the vibe in 3 words", type: "text", placeholder: "e.g., loud, family, lowriders", maxLength: 120 },
      ],
    },
    {
      key: "history",
      title: "Activities & History",
      fields: [
        {
          key: "activities",
          label: "Activities at the event",
          type: "checkboxes",
          options: ["live_music", "stunt_show", "races", "vendors", "food_trucks", "kids_zone", "bike_show", "car_show", "boat_displays", "sponsor_booths", "other"],
        },
        { key: "history", label: "Brief history of the event", type: "textarea", maxLength: 1500, rows: 3 },
        { key: "media_coverage", label: "Past media coverage (links / outlets)", type: "textarea", maxLength: 800, rows: 3 },
      ],
    },
    {
      key: "permission",
      title: "Filming & Sponsorship",
      fields: [
        {
          key: "filming_permission",
          label: "Are you open to AutoMotoVille filming at your event?",
          type: "radio",
          required: true,
          options: ["Yes", "Maybe — let's discuss", "No"],
        },
        {
          key: "background_use",
          label: "Are you open to your event being used as a background for Culture Files / reenactments?",
          type: "radio",
          required: true,
          options: ["Yes", "Maybe", "No"],
        },
        {
          key: "sponsorship",
          label: "Open to sponsor activations from AutoMotoVille brand partners?",
          type: "radio",
          required: true,
          options: ["Yes", "Maybe", "No"],
        },
        { key: "tags", label: "Tags", type: "tags", tagPool: ["motorcycle", "car", "truck", "boat", "national", "regional", "family", "club", "promoter", "viral"] },
      ],
    },
    {
      key: "uploads",
      title: "Show Us",
      subtitle: "Flyers, photos from past events, short clips — anything that captures the energy.",
      fields: [
        {
          key: "photos",
          label: "Photos (flyers, crowd shots, hero images)",
          type: "photos",
          minPhotos: 0,
          maxPhotos: 8,
        },
        {
          key: "video",
          label: "Promo or recap video (or paste a link)",
          type: "video",
          required: false,
          note: "Optional but strongly recommended.",
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: f.tags || [],
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      event_name: f.event_name || "",
      event_type: f.event_type || [],
      frequency: f.frequency || "",
      attendance: Number(f.attendance) || 0,
      uniqueness: f.uniqueness || "",
      vibe: f.vibe || "",
      activities: f.activities || [],
      history: f.history || "",
      media_coverage: f.media_coverage || "",
      filming_permission: f.filming_permission || "",
      background_use: f.background_use || "",
      sponsorship: f.sponsorship || "",
    },
  }),
  success: {
    title: "Event submitted.",
    message:
      "Producers will review your event. If it fits a Culture Files episode or a sponsor activation, we'll be in touch.",
  },
};

// ============================================================
// Reenactment Talent
// ============================================================
export const REENACTMENT_CONFIG = {
  kind: "reenactment",
  intro: {
    eyebrow: "REENACTMENT TALENT",
    headline: "Bring Real Stories to Life",
    blurb:
      "Actors, riders, drivers, builders, real personalities. No experience required — but presence matters. Used across AutoMotoVille originals.",
    cta: "Join Reenactment Casting",
  },
  submitLabel: "Join Casting",
  steps: [
    {
      key: "about",
      title: "About You",
      fields: [
        { key: "full_name", label: "Full Name", type: "text", required: true, maxLength: 120 },
        { key: "stage_name", label: "Stage / Screen Name", type: "text", maxLength: 80 },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone", type: "tel", required: true },
        { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
        { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
        { key: "age", label: "Age", type: "number", required: true, min: 18, max: 99 },
      ],
    },
    {
      key: "physical",
      title: "Physical Details",
      fields: [
        { key: "height", label: "Height", type: "text", placeholder: "e.g., 5'10\"", maxLength: 20 },
        { key: "build", label: "Build", type: "select", options: ["slim", "athletic", "average", "muscular", "heavy"] },
        { key: "ethnicity", label: "Ethnicity (self-identified)", type: "text", maxLength: 80 },
        { key: "hair", label: "Hair", type: "text", maxLength: 60 },
        { key: "eyes", label: "Eyes", type: "text", maxLength: 30 },
        { key: "tattoos_visible", label: "Visible tattoos?", type: "radio", options: ["None", "A few", "Many", "Sleeves / full"] },
        { key: "languages", label: "Languages spoken", type: "text", placeholder: "English, Spanish…", maxLength: 200 },
      ],
    },
    {
      key: "experience",
      title: "Experience & Comfort",
      fields: [
        {
          key: "acting_experience",
          label: "Acting experience",
          type: "radio",
          required: true,
          options: ["None", "Some indie / student", "Pro background", "SAG-eligible / SAG"],
        },
        {
          key: "on_camera_comfort",
          label: "Comfort on camera",
          type: "radio",
          required: true,
          options: ["Very comfortable", "Comfortable", "Newer to it"],
        },
        {
          key: "improv",
          label: "Comfortable with improv?",
          type: "radio",
          options: ["Yes", "Sometimes", "Prefer scripted"],
        },
        { key: "reel_url", label: "Demo reel / portfolio link", type: "text", maxLength: 300 },
      ],
    },
    {
      key: "skills",
      title: "Skills",
      fields: [
        {
          key: "skills",
          label: "Skills (pick all that apply)",
          type: "checkboxes",
          options: ["motorcycle_riding", "race_car_driving", "manual_truck", "boat_handling", "stunt", "fight_choreo", "weapons_safety", "mechanic_work", "fabrication", "welding", "horseback", "dance", "DJ", "other"],
        },
        {
          key: "tags",
          label: "Tags",
          type: "tags",
          tagPool: [
            "biker_look",
            "luxury_look",
            "rider",
            "driver",
            "mechanic",
            "fabricator",
            "club_member",
            "stunt",
            "speaks_spanish",
            "on_camera",
          ],
        },
      ],
    },
    {
      key: "scenes",
      title: "Scene Comfort",
      subtitle:
        "Be honest. We'll only book you for scenes you opted in to.",
      fields: [
        { key: "scene_partial_nudity", label: "Partial nudity (within union rules)?", type: "radio", required: true, options: ["Yes", "Discuss", "No"] },
        { key: "scene_violence", label: "Stylized violence?", type: "radio", required: true, options: ["Yes", "Discuss", "No"] },
        { key: "scene_intimate", label: "Romantic / intimate scenes?", type: "radio", required: true, options: ["Yes", "Discuss", "No"] },
        { key: "scene_drug_props", label: "Drug references / props?", type: "radio", required: true, options: ["Yes", "Discuss", "No"] },
        { key: "scene_law_enforcement", label: "Law-enforcement / arrest reenactment?", type: "radio", required: true, options: ["Yes", "Discuss", "No"] },
      ],
    },
    {
      key: "uploads",
      title: "Photos & Demo Video",
      subtitle: "2–6 photos · short demo video. No heavy filters.",
      fields: [
        {
          key: "photos",
          label: "Headshots + lifestyle (no filters)",
          type: "photos",
          required: true,
          minPhotos: 2,
          maxPhotos: 6,
          note: "1 clean headshot + 2-5 lifestyle / in-character shots is ideal.",
        },
        {
          key: "video",
          label: "60-second demo (or reel link)",
          type: "video",
          required: true,
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: f.tags || [],
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      stage_name: f.stage_name || "",
      age: Number(f.age) || null,
      height: f.height || "",
      build: f.build || "",
      ethnicity: f.ethnicity || "",
      hair: f.hair || "",
      eyes: f.eyes || "",
      tattoos_visible: f.tattoos_visible || "",
      languages: f.languages || "",
      acting_experience: f.acting_experience || "",
      on_camera_comfort: f.on_camera_comfort || "",
      improv: f.improv || "",
      reel_url: f.reel_url || "",
      skills: f.skills || [],
      scene_partial_nudity: f.scene_partial_nudity || "",
      scene_violence: f.scene_violence || "",
      scene_intimate: f.scene_intimate || "",
      scene_drug_props: f.scene_drug_props || "",
      scene_law_enforcement: f.scene_law_enforcement || "",
    },
  }),
  success: {
    title: "Welcome to the talent pool.",
    message:
      "Producers will reach out when a role fits. Keep your photos and reel current — refresh whenever your look changes.",
  },
};

// ============================================================
// Food Vendors
// ============================================================
const UNIVERSAL_ABOUT_FIELDS = [
  { key: "full_name", label: "Full Name", type: "text", required: true, maxLength: 120 },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "phone", label: "Phone Number", type: "tel", required: true, maxLength: 40 },
  { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
  { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
  {
    key: "social_handle",
    label: "Instagram / Social Handle",
    type: "text",
    placeholder: "@yourhandle",
    maxLength: 200,
  },
];

export const FOOD_CONFIG = {
  kind: "food",
  intro: {
    eyebrow: "STREET EATS & ENGINES",
    headline: "Think Your Food Hits?",
    blurb:
      "Submit your food truck, catering setup, or kitchen. If selected, an AutoMotoVille panel will pull up and decide if it hits like you say it does. If it's hitting, the culture will know.",
    cta: "Submit Your Food Business",
  },
  submitLabel: "Submit Business",
  steps: [
    {
      key: "about",
      title: "About You",
      subtitle: "How a producer reaches you when an event lines up.",
      fields: UNIVERSAL_ABOUT_FIELDS,
    },
    {
      key: "business",
      title: "About Your Business",
      fields: [
        {
          key: "business_name",
          label: "Business Name",
          type: "text",
          required: true,
          maxLength: 160,
        },
        {
          key: "food_type",
          label: "Type of Food",
          type: "text",
          required: true,
          placeholder: "BBQ, soul food, tacos, smashburgers, vegan…",
          maxLength: 160,
        },
        {
          key: "events_attended",
          label: "Events you attend",
          type: "textarea",
          required: true,
          rows: 3,
          maxLength: 1000,
          placeholder: "MC annuals, car meets, Sunday day parties…",
        },
        {
          key: "tags",
          label: "Tags",
          type: "tags",
          tagPool: ["bbq", "soul_food", "tacos", "vegan", "mobile_kitchen", "halal", "desserts"],
        },
      ],
    },
    {
      key: "uploads",
      title: "Show Us",
      subtitle: "Photos of your food and setup. No filters — let it look real.",
      fields: [
        {
          key: "photos",
          label: "Photos of your food / setup",
          type: "photos",
          required: true,
          minPhotos: 2,
          maxPhotos: 8,
        },
        {
          key: "video",
          label: "Optional short video (or link)",
          type: "video",
          required: false,
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: f.tags || [],
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      social_handle: f.social_handle || "",
      business_name: f.business_name || "",
      food_type: f.food_type || "",
      events_attended: f.events_attended || "",
    },
  }),
  success: {
    title: "Submitted.",
    message:
      "A producer will reach out when an event or feature lines up with your kitchen.",
  },
};

// ============================================================
// Performers — Stage for the Streets
// ============================================================
export const PERFORMERS_CONFIG = {
  kind: "performers",
  intro: {
    eyebrow: "STAGE FOR THE STREETS",
    headline: "The streets decide who's next.",
    tagline:
      "This is your chance to perform in front of a live crowd and get real exposure in the culture.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/xubdvo1j_117F3023-0ADA-49D2-869F-0F10D3E4BDF0.png",
    pitch: [
      "Stage For The Streets is a live performance-based reality series that discovers and elevates talent directly from the culture — motorcycle events, car shows, truck meets, and street festivals.",
      "We don't just showcase talent. We bring the stage to the culture. We bring the crowd to the performers. And we turn real environments into entertainment.",
      "Selected performers will get the chance to perform in front of a live audience and a panel — but the real decision? The crowd decides.",
    ],
    standards:
      "30-second performance video with clear audio, no filters. Your name / stage name, city & state, social media, and any performance experience.",
    accepts: [
      "Rappers",
      "Singers",
      "High-Energy Performers",
      "Stage Presence & Crowd Control",
      "Authentic to the Culture",
    ],
    process: [
      { title: "Submit Your Application", body: "Fill out your details + social links." },
      { title: "Upload a 30-sec Clip", body: "Rap or singing. Clear audio, no filters." },
      { title: "Get Invited to Perform", body: "If selected, you'll be invited to perform live." },
      { title: "Hit the Stage", body: "Perform in front of a real crowd and a panel." },
      { title: "The Crowd Decides", body: "Audience reaction helps decide who moves forward." },
      { title: "Get Booked", body: "Top talent earns exposure + real booking opportunities." },
    ],
    earn:
      "This isn't just a performance — it's your opportunity. If you're tired of being overlooked, this is where the culture finally sees you.",
    cta: "Start Application",
  },
  submitLabel: "Submit My Performance",
  steps: [
    {
      key: "qualifier",
      title: "Let's see if you're a fit",
      subtitle: "Two quick questions so we know where to place you.",
      fields: [
        {
          key: "performer_type",
          label: "What type of performer are you?",
          type: "radio",
          required: true,
          options: ["Rapper", "Singer", "Both"],
        },
        {
          key: "performed_before",
          label: "Have you performed before?",
          type: "radio",
          required: true,
          options: ["Yes", "No (I'm ready now)"],
        },
      ],
    },
    {
      key: "about",
      title: "Tell us about you",
      fields: [
        { key: "full_name", label: "Full Name", type: "text", required: true, maxLength: 120 },
        {
          key: "stage_name",
          label: "Stage Name (if different)",
          type: "text",
          maxLength: 120,
        },
        { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
        { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
        { key: "phone", label: "Phone Number", type: "tel", required: true, maxLength: 40 },
        { key: "email", label: "Email Address", type: "email", required: true },
        {
          key: "social_handle",
          label: "Instagram / Social Media",
          type: "text",
          placeholder: "@yourhandle",
          maxLength: 200,
        },
      ],
    },
    {
      key: "culture",
      title: "Your connection to the culture",
      fields: [
        {
          key: "venues",
          label: "Where do you usually perform? (Select all that apply)",
          type: "checkboxes",
          options: [
            "Bike events",
            "Car shows",
            "Truck shows",
            "Street festivals",
            "Online only",
            "Never performed before",
          ],
        },
        {
          key: "crew",
          label: "Are you affiliated with any clubs, teams, or crews?",
          type: "textarea",
          rows: 3,
          maxLength: 500,
          placeholder: "Short answer — name + your role.",
        },
      ],
    },
    {
      key: "video",
      title: "Show us what you got",
      subtitle:
        "Upload a 30-second video of you rapping or singing. Clear audio. No heavy filters. Real performance energy.",
      fields: [
        {
          key: "video",
          label: "30-second performance video",
          type: "video",
          required: true,
          note: "Live phone audio is fine — we want to feel the energy, not a polished mix.",
        },
      ],
    },
    {
      key: "personality",
      title: "Why you?",
      subtitle: "This is where stars get found.",
      fields: [
        {
          key: "why_you",
          label: "Why should we pick you?",
          type: "textarea",
          required: true,
          rows: 4,
          maxLength: 1000,
        },
        {
          key: "what_different",
          label: "What makes you different from other performers?",
          type: "textarea",
          required: true,
          rows: 4,
          maxLength: 1000,
        },
        {
          key: "stage_ready",
          label: "Are you ready to perform in front of a live crowd?",
          type: "radio",
          required: true,
          options: ["Yes", "Absolutely", "I was born for this"],
        },
      ],
    },
    {
      key: "commitment",
      title: "Final step",
      subtitle: "Confirm a few things so we know you're all in.",
      fields: [
        {
          key: "agreements",
          label: "Please confirm all of the following",
          type: "checkboxes",
          required: true,
          minChecked: 3,
          options: [
            "I understand I may be filmed for television",
            "I am available for live performances if selected",
            "I agree to participate in a filmed production",
          ],
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: [
      ...(f.performer_type ? [f.performer_type.toLowerCase()] : []),
      ...(f.venues || []).map((v) =>
        v.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
      ),
    ].filter(Boolean),
    photos: [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      stage_name: f.stage_name || "",
      social_handle: f.social_handle || "",
      performer_type: f.performer_type || "",
      performed_before: f.performed_before || "",
      venues: f.venues || [],
      crew: f.crew || "",
      why_you: f.why_you || "",
      what_different: f.what_different || "",
      stage_ready: f.stage_ready || "",
      agreements: f.agreements || [],
    },
  }),
  success: {
    title: "You're in.",
    message:
      "If selected, our team will contact you for the next step. Stay ready — opportunities move fast.\n\nFollow us on Instagram for casting announcements and live event drops.",
  },
};

// ============================================================
// Independent Creators & Filmmakers
// ============================================================
export const CONTENT_CREATORS_CONFIG = {
  kind: "content-creators",
  intro: {
    eyebrow: "INDEPENDENT CREATORS & FILMMAKERS",
    headline: "Bring your COMPLETED content. We'll bring it to the culture.",
    tagline:
      "We're accepting fully completed content from independent creators. This is NOT a hiring call — this is for creators who already have finished, edited, and ready-to-distribute content.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/e955el2o_28F03F70-8EC4-4458-9589-B440F423EC78.png",
    pitch: [
      "Are you tired of trying to get your content on other platforms and being turned down because they don't \"get it\"?",
      "At AutoMotoVille, we understand that not every platform is built for every voice. We're not here to shut down your creativity — we're here to give it a place to live.",
      "If your content is high-quality, fully produced, and tells a story that connects to the culture, we want to see it.",
      "Just because one platform doesn't accept it doesn't mean it doesn't have value. If the audience connects, it wins. And when it wins — you earn from your views.",
    ],
    standards:
      "Strong visuals, clear audio, and complete storytelling are required.",
    accepts: [
      "Original Shows & Docuseries",
      "Short Films & Features",
      "Real Stories & Lifestyle",
      "Music Videos & Performances",
      "Fully Edited Digital Content",
    ],
    process: [
      {
        title: "Submit For Approval",
        body:
          "Tell us about your content and include a trailer or sample of your completed project.",
      },
      {
        title: "Review & Approval",
        body:
          "Our team reviews your submission to make sure it fits the culture.",
      },
      {
        title: "Upload & Go Live",
        body:
          "If approved, you'll submit your full content and we bring it to the platform.",
      },
    ],
    earn:
      "When the audience connects with your content, you earn from your views. AutoMotoVille is built so creators win when the culture wins.",
    earnTitle: "You create. We distribute. You earn.",
    note: {
      label: "NOT READY YET?",
      body:
        "If you don't have completed content but have a strong idea and need help bringing your vision to life, we offer a separate process. You can inquire about production support, filming, and development opportunities — mention it in your submission.",
    },
    cta: "Submit Your Project For Approval",
  },
  submitLabel: "Submit For Approval",
  steps: [
    {
      key: "about",
      title: "About You",
      fields: UNIVERSAL_ABOUT_FIELDS,
    },
    {
      key: "project",
      title: "About Your Project",
      fields: [
        {
          key: "project_title",
          label: "Project Title",
          type: "text",
          required: true,
          maxLength: 160,
        },
        {
          key: "project_type",
          label: "Type",
          type: "radio",
          required: true,
          options: ["Show", "Podcast", "Film", "Web series", "Documentary", "Other"],
        },
        {
          key: "project_description",
          label: "Short description",
          type: "textarea",
          required: true,
          rows: 5,
          maxLength: 2000,
          placeholder:
            "Logline + 2-3 lines on what makes this project culture-real and unmissable.",
        },
        {
          key: "tags",
          label: "Tags",
          type: "tags",
          tagPool: ["scripted", "unscripted", "docuseries", "comedy", "drama", "competition", "podcast", "short_form"],
        },
      ],
    },
    {
      key: "uploads",
      title: "Show Us a Sample",
      fields: [
        {
          key: "video",
          label: "Sample video / sizzle / pilot (or link)",
          type: "video",
          required: true,
          note: "External link is fine — YouTube, Vimeo, Drive.",
        },
        {
          key: "photos",
          label: "Key art / stills (optional)",
          type: "photos",
          minPhotos: 0,
          maxPhotos: 6,
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: f.tags || [],
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      social_handle: f.social_handle || "",
      project_title: f.project_title || "",
      project_type: f.project_type || "",
      project_description: f.project_description || "",
    },
  }),
  success: {
    title: "Pitch received.",
    message:
      "Our development team will review. If your project fits the AutoMotoVille slate, we'll be in touch.",
  },
};

// ============================================================
// Love in the Fast Lane (Dating)
// ============================================================
export const LOVE_CONFIG = {
  kind: "dating",
  intro: {
    eyebrow: "LOVE IN THE FAST LANE",
    headline: "Shoot your shot.",
    tagline:
      "Dating in the culture can be complicated — until now. A dating experience built for bikers, truckers, car lovers, boaters, builders, and riders who live the lifestyle.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tstxxcvb_63366FCD-3430-4F13-B40B-E09872280990.png",
    pitch: [
      "We're casting real people open to finding real connections — whether you're looking for love, chemistry, or just seeing where the road takes you.",
      "Selected applicants may also be considered for other AutoMotoVille originals.",
    ],
    cta: "Start My Application",
  submitLabel: "Shoot Your Shot",
  },
  steps: [
    {
      key: "lane",
      title: "Your Lane",
      fields: [
        {
          key: "lanes",
          label: "Your Lane (Choose one or more)",
          type: "checkboxes",
          required: true,
          options: ["Trucking", "Biking", "Cars", "Boating"],
        },
        {
          key: "dating_vibe",
          label: "Your Dating Vibe",
          type: "textarea",
          required: true,
          rows: 5,
          maxLength: 1500,
          placeholder:
            "What makes you stand out in your lane — and what are you looking for in someone riding with you?",
        },
        {
          key: "years_in_culture",
          label: "Years in the Culture",
          type: "number",
          required: true,
          min: 0,
          max: 80,
        },
      ],
    },
    {
      key: "relationship",
      title: "Relationship Energy",
      fields: [
        {
          key: "looking_for",
          label: "What are you looking for?",
          type: "radio",
          required: true,
          options: [
            "Real relationship",
            "Something fun",
            "Open to whatever happens",
            "Not sure yet",
          ],
        },
        {
          key: "status",
          label: "Are you currently single?",
          type: "radio",
          required: true,
          options: ["Yes", "It's complicated", "No (but open 👀)"],
        },
        {
          key: "attracted_to",
          label: "What type of person are you attracted to?",
          type: "textarea",
          required: true,
          rows: 4,
          maxLength: 1000,
        },
        {
          key: "turn_off",
          label: "What's an instant turn-off for you?",
          type: "textarea",
          required: true,
          rows: 3,
          maxLength: 800,
          placeholder: "Be honest — this creates great casting moments.",
        },
        {
          key: "green_flags",
          label: "What makes you instantly interested in someone?",
          type: "textarea",
          required: true,
          rows: 3,
          maxLength: 800,
        },
      ],
    },
    {
      key: "personality",
      title: "Personality Check",
      fields: [
        {
          key: "drama_level",
          label: "Be honest… do you bring peace or a little chaos?",
          type: "radio",
          required: true,
          options: [
            "I'm calm and drama-free",
            "I mind my business… mostly",
            "I match energy",
            "I bring the chaos 😈",
          ],
        },
        {
          key: "multi_dating",
          label: "Have you ever talked to multiple people at the same time while dating?",
          type: "radio",
          required: true,
          options: ["Yes 😏", "No", "Depends"],
        },
        {
          key: "relationship_endings",
          label: "What usually ends your relationships?",
          type: "textarea",
          required: true,
          rows: 4,
          maxLength: 1000,
          placeholder: "Be honest — this is where we find the real you.",
        },
        {
          key: "why_lucky",
          label: "Why would someone be lucky to date you?",
          type: "textarea",
          required: true,
          rows: 4,
          maxLength: 1000,
        },
        {
          key: "competition_energy",
          label: "If someone else is interested in your match… what are you doing?",
          type: "radio",
          required: true,
          options: [
            "Let them have them",
            "I'll step my game up",
            "I'm not competing for anyone",
            "I always win 😎",
          ],
        },
      ],
    },
    {
      key: "camera",
      title: "On Camera",
      fields: [
        {
          key: "camera_experience",
          label: "Have you ever been on camera before?",
          type: "radio",
          required: true,
          options: ["Yes", "No", "Social media only"],
        },
        {
          key: "video",
          label: "Upload a 30–60 second video or drop a link",
          type: "video",
          required: true,
          note: "Tell us: who you are, your vibe, and why you should be cast.",
        },
      ],
    },
    {
      key: "final",
      title: "Final Touch",
      subtitle: "Last thing — how we reach you and your closing line.",
      fields: [
        { key: "full_name", label: "Full Name", type: "text", required: true, maxLength: 120 },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "phone", label: "Phone Number", type: "tel", required: true, maxLength: 40 },
        { key: "city", label: "City", type: "text", required: true, maxLength: 80 },
        { key: "state", label: "State", type: "text", required: true, maxLength: 80 },
        {
          key: "social_handle",
          label: "Instagram / Social Media",
          type: "text",
          placeholder: "@yourhandle",
          maxLength: 200,
        },
        {
          key: "photos",
          label: "Upload 2–3 recent photos (no filters)",
          type: "photos",
          required: true,
          minPhotos: 2,
          maxPhotos: 3,
        },
        {
          key: "final_hook",
          label: "Why should we cast you on Love in the Fast Lane?",
          type: "textarea",
          required: true,
          rows: 5,
          maxLength: 1500,
          placeholder: "Your closing line — make it count.",
        },
      ],
    },
  ],
  buildPayload: (f) => ({
    full_name: f.full_name || "",
    email: f.email || null,
    phone: f.phone || "",
    city: f.city || "",
    state: f.state || "",
    tags: [
      ...(f.lanes || []).map((l) => l.toLowerCase()),
      ...(f.looking_for ? [f.looking_for.toLowerCase().replace(/[^a-z0-9]+/g, "_")] : []),
      ...(f.status ? [f.status.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")] : []),
    ].filter(Boolean),
    photos: f.photos || [],
    video_file_id: f.video?.video_file_id || null,
    video_url: f.video?.video_url || null,
    data: {
      social_handle: f.social_handle || "",
      years_in_culture: f.years_in_culture || "",
      dating_vibe: f.dating_vibe || "",
      looking_for: f.looking_for || "",
      status: f.status || "",
      attracted_to: f.attracted_to || "",
      turn_off: f.turn_off || "",
      green_flags: f.green_flags || "",
      drama_level: f.drama_level || "",
      multi_dating: f.multi_dating || "",
      relationship_endings: f.relationship_endings || "",
      why_lucky: f.why_lucky || "",
      competition_energy: f.competition_energy || "",
      camera_experience: f.camera_experience || "",
      final_hook: f.final_hook || "",
    },
  }),
  success: {
    title: "Shot taken.",
    message:
      "Our casting team will review your tape. If you're a fit, we'll reach out to schedule a chemistry read.\n\nKeep your phone close.",
  },
};

export const FLOW_CONFIGS = {
  "culture-files": CULTURE_FILES_CONFIG,
  events: EVENTS_CONFIG,
  reenactment: REENACTMENT_CONFIG,
  food: FOOD_CONFIG,
  performers: PERFORMERS_CONFIG,
  "content-creators": CONTENT_CREATORS_CONFIG,
  dating: LOVE_CONFIG,
};
